import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-async-hook'
import { useTheme } from '@emotion/react'
import { AssetListItem, SearchInputForm } from '@kc/alphabrands-component-library'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faSortAmountDown, faSortAmountUp, faArrowUp } from '@fortawesome/free-solid-svg-icons'

import useAPIClient from '../../hooks/useAPIClient'
import { Asset } from '../../api/types'
import {
  CustomerList,
  HeaderContainer,
  SortButton,
  ListInfoRow,
  FilterRow,
  HeaderText,
  SearchContainer,
} from './AssetsPage.styles'
import Loader from '../../components/Loader'
import DataNotFound from '../../components/DataNotFound'
import PageContent from '../../styles/PageContent'
import {
  useFilters,
  useLabelAutocomplete,
  getStates,
  getYearRange,
  useFiltersUrlSync,
  groupedBySalesOrderNo,
  prepareAssetsFiltersAsParams,
} from './helpers'
import { Breadcrumbs } from '../../components/BreadcrumbsNav'
import FilterInput from '../../components/FilterInput'
import FiltersArea from '../../components/FiltersArea'
import StandardError from '../../components/StandardError'
import { FilterProps } from './types'
import countryCodes from './CountryCodes'
import productTypes from '../../constants/productTypes'
import useRandomPoll from '../../hooks/useRandomPoll'
import Button from '../../components/Button'

const limit = 50
const AssetsList: React.FC<{ customerId: string; isInternal?: boolean }> = ({
  customerId,
  isInternal,
}) => {
  const theme = useTheme()
  const apiClient = useAPIClient()
  const { t } = useTranslation()
  const { filters, handleAddFilter, handleDeleteFilter, clearFilters, setFilters } = useFilters()
  const effectCalls = useRef(0)
  const isFirstRender = effectCalls.current === 0
  const { initFiltersFromUrl, syncFiltersToUrl } = useFiltersUrlSync(filters, setFilters)
  useRandomPoll({ page: 'myequipment', maxNumber: 4 })

  const [searchTerm, setSearchTerm] = useState('')
  const [sortDirection, setSortDirection] = useState('desc')
  const [assetsList, setAssetsList] = useState<Asset[]>([])
  const [loading, setLoading] = useState(false)
  const [refreshExistingList, setRefreshExistingList] = useState(false)
  const [error, setError] = useState('')
  const [offset, setOffset] = useState(0)

  const fetchAssetsList = useCallback(
    async (_customerId: string, clearExistingList: boolean = false) => {
      try {
        setLoading(true)
        setError('')
        setRefreshExistingList(clearExistingList)

        const filtersAsParams = prepareAssetsFiltersAsParams(filters)

        // TODO: Uncomment this for server side search
        // if (searchTerm !== '') filtersAsParams['search'] = searchTerm
        const { data } = await apiClient.listAssets(
          _customerId,
          limit,
          clearExistingList ? 0 : offset,
          sortDirection,
          filtersAsParams,
        )
        clearExistingList ? setAssetsList(data) : setAssetsList([...assetsList, ...data])
      } catch (err) {
        setError(t('something_went_wrong'))
      } finally {
        setLoading(false)
        setRefreshExistingList(false)
      }
    },
    [apiClient, offset, assetsList, t, sortDirection, filters],
  )

  const asyncCustomerName = useAsync<string>(
    async (_customerId: string) => {
      try {
        const { data } = await apiClient.getCustomerById(_customerId)
        return data[0].accountname
      } catch (err) {
        throw new Error(t('something_went_wrong'))
      }
    },
    [customerId],
  )

  let bottomBoundaryRef: any = useRef(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollObserver = (node: HTMLElement) => {
    new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.intersectionRatio > 0) {
          setOffset(offset + limit)
        }
      })
    }).observe(node)
  }

  useEffect(() => {
    if (!isFirstRender && assetsList.length <= offset) {
      fetchAssetsList(customerId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, customerId])

  useEffect(() => {
    if (isFirstRender) {
      initFiltersFromUrl()
    } else {
      syncFiltersToUrl()
      setOffset(0)
      fetchAssetsList(customerId, true)
    }
    effectCalls.current++
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, filters])

  useEffect(() => {
    const { current } = bottomBoundaryRef
    if (current) {
      scrollObserver(current)
    }
  })

  let filteredAssets = assetsList

  // TODO: Remove this block when server side search implemented
  if (assetsList && searchTerm) {
    filteredAssets = assetsList.filter(
      ({ SalesOrderNo, SerialNumber, InternalSalesOrderNo }) =>
        SalesOrderNo?.match(new RegExp(searchTerm, 'ig')) ||
        SerialNumber?.match(new RegExp(searchTerm, 'ig')) ||
        InternalSalesOrderNo?.match(new RegExp(searchTerm, 'ig')),
    )
  }
  const assetsBySalesOrderNo = filteredAssets
    ? Object.entries(groupedBySalesOrderNo(filteredAssets, true))
    : []

  const showBottomBoundary = () => !loading && assetsList.length >= limit + offset
  const getAssetsListNumber = () =>
    assetsBySalesOrderNo.length !== 0 && assetsBySalesOrderNo.length % 50 === 0
      ? `${assetsBySalesOrderNo.length} +`
      : `${assetsBySalesOrderNo.length}`
  const orderNoFormatter = (orderNo: string): string => {
    return orderNo.substr(0, 3) === 'IO-' ? orderNo.substr(3) : orderNo
  }
  return (
    <>
      <Breadcrumbs>
        <Breadcrumbs.Crumb
          to="/my-partners-and-equipment"
          text={isInternal ? t('my_partners_and_equipment') : t('my_equipment')}
        />
        <Breadcrumbs.Crumb text={asyncCustomerName.result} />
      </Breadcrumbs>
      <PageContent>
        <HeaderContainer>
          <HeaderText>{asyncCustomerName.result}</HeaderText>
          <SearchContainer>
            <SearchInputForm
              onChange={(v) => setSearchTerm(v)}
              id="assets-search-input"
              label={t('search_by_order_number')}
            />
          </SearchContainer>
        </HeaderContainer>
        <Filters
          filters={filters}
          onFilterAdd={handleAddFilter}
          clearFilters={clearFilters}
          onFilterDeleteClick={handleDeleteFilter}
          customerId={customerId}
        />
        {refreshExistingList && <Loader loadingText={t('loading')} />}
        {error && <StandardError />}
        {!refreshExistingList && !error && assetsList && (
          <>
            <ListInfoRow>
              <p>{`${t('sales_orders')} (${getAssetsListNumber()})`}</p>
              <SortButton
                aria-label={t('sort')}
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              >
                <Icon
                  icon={sortDirection === 'desc' ? faSortAmountDown : faSortAmountUp}
                  size="2x"
                />
              </SortButton>
            </ListInfoRow>

            <CustomerList data-testid="asset-list">
              {assetsBySalesOrderNo?.map(([orderNo, assetAttributes], index) => {
                const assetDescription = [
                  ...new Set(
                    assetAttributes?.map((asset) => asset.AssetDescription?.toUpperCase()),
                  ),
                ].join(' / ')
                return (
                  <Link
                    key={index}
                    to={`/my-partners-and-equipment/${customerId}/order/${orderNo}`}
                  >
                    <AssetListItem
                      key={orderNo}
                      name={orderNoFormatter(orderNo)}
                      description={assetDescription}
                      titleColor={theme.listItem.titleColor}
                      descriptionColor={theme.listItem.descriptionColor}
                    />
                  </Link>
                )
              })}
              {!showBottomBoundary() && !loading && assetsBySalesOrderNo.length > limit && (
                <Button
                  inverted
                  onClick={() =>
                    window.scrollTo({
                      top: 160,
                      left: 0,
                      behavior: 'smooth',
                    })
                  }
                >
                  <Icon icon={faArrowUp} />
                  {t('back_to_top')}
                </Button>
              )}

              {!loading && !error && assetsBySalesOrderNo.length === 0 && (
                <DataNotFound notFoundText={t('no_assets_error')} />
              )}
            </CustomerList>
            {loading && offset >= 50 && <Loader loadingText={t('loading')} />}
            {showBottomBoundary() && (
              <div
                id="page-bottom-boundary"
                style={{ visibility: 'hidden' }}
                ref={bottomBoundaryRef}
              ></div>
            )}
          </>
        )}
      </PageContent>
    </>
  )
}

const Filters: React.FC<FilterProps & { customerId: string }> = ({
  onFilterAdd,
  filters,
  onFilterDeleteClick,
  clearFilters,
  customerId,
}) => {
  const { t } = useTranslation()
  const [asyncCustomerLabelSearch, setCustomerSearchText] = useLabelAutocomplete(
    customerId,
    'customer',
  )
  const [asyncCustomLabelSearch, setCustomSearchText] = useLabelAutocomplete(customerId, 'custom')
  const country = filters.find((filter) => filter.type === 'country')?.filter.toLowerCase()
  const states = getStates(country)
  const countryDisabled = filters.some((filter) => filter.type === 'country')
  const theme = useTheme()

  return (
    <FiltersArea
      filters={filters}
      onFilterDeleteClick={onFilterDeleteClick}
      clearFilters={clearFilters}
      labelBackgroundColor={theme.button.primary.color}
      labelTextColor={theme.button.primary.textColor}
      linkColor={theme.linkColor}
      linkHoverColor={theme.linkHoverColor}
    >
      <FilterRow>
        <FilterInput
          type="productType"
          title={t('product_type')}
          items={Object.keys(productTypes)}
          onItemSelect={onFilterAdd}
        />
        <FilterInput
          type="country"
          title={t('country')}
          items={Object.values(countryCodes)}
          disabled={countryDisabled}
          onItemSelect={onFilterAdd}
        />
        <FilterInput
          type="state"
          title={t('state')}
          items={states}
          disabled={states.length === 0}
          onItemSelect={onFilterAdd}
        />
      </FilterRow>
      <FilterRow>
        <FilterInput
          type="manufacturedYear"
          title={t('manufactured_year')}
          items={getYearRange()}
          onItemSelect={onFilterAdd}
        />
        <FilterInput
          type="customer"
          title={t('end_customer')}
          items={asyncCustomerLabelSearch.result?.map((label) => label.label) || []}
          onInputChange={(inputValue) => setCustomerSearchText(inputValue)}
          onItemSelect={onFilterAdd}
        />

        <FilterInput
          type="custom"
          title={t('custom_label')}
          items={asyncCustomLabelSearch.result?.map((label) => label.label) || []}
          onInputChange={(inputValue) => setCustomSearchText(inputValue)}
          onItemSelect={onFilterAdd}
        />
      </FilterRow>
    </FiltersArea>
  )
}

export default AssetsList
